import { getProductSizesApi, getOfferTypesApi, getProductCategoriesApi } from 'api';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useDataStore = create(
    persist((set, get) => ({
        productSizes: [],
        offerTypes: [],
        subCatData: [],
        isLoadingSizes: false,
        isLoadingOfferTypes: false,
        isLoadingSubCat: false,
        errorSizes: null,
        sizesCacheTime: null, // Track when sizes were last fetched
        
        refreshSizes: async () => {
            set({ isLoadingSizes: true, errorSizes: null });
            try {
                const sizeRes = await getProductSizesApi();
                if (!sizeRes.success) {
                    throw new Error('Failed to fetch product sizes');
                }
                const sizes = sizeRes.payload.map(({ ext_id, size_c, whole_num_f }) => ({
                    id: ext_id,
                    title: size_c,
                    whole_num_f,
                }));
                set({ productSizes: sizes, isLoadingSizes: false, sizesCacheTime: Date.now() });
                return { success: true, message: 'Product sizes refreshed successfully' };
            } catch (error) {
                set({ errorSizes: error.message, isLoadingSizes: false });
                return { success: false, message: error.message };
            }
        },
        
        // Returns true if sizes need refresh (stale or empty)
        isSizesCacheStale: () => {
            const state = get();
            const CACHE_TTL_MS = 48 * 60 * 60 * 1000; // 48 hours
            if (state.productSizes.length === 0) return true;
            if (!state.sizesCacheTime) return true;
            return Date.now() - state.sizesCacheTime > CACHE_TTL_MS;
        },
        
        // Refresh in background without blocking (for stale cache)
        backgroundRefreshSizes: async () => {
            // Don't set isLoadingSizes—user sees cached data while we fetch
            try {
                const sizeRes = await getProductSizesApi();
                if (sizeRes.success) {
                    const sizes = sizeRes.payload.map(({ ext_id, size_c, whole_num_f }) => ({
                        id: ext_id,
                        title: size_c,
                        whole_num_f,
                    }));
                    set({ productSizes: sizes, sizesCacheTime: Date.now(), errorSizes: null });
                }
            } catch (error) {
                console.warn('Background sizes refresh failed:', error.message);
            }
        },
        
        fetchOfferTypes: async () => {
            // TODO: What's the way to invalidate this for all users during update?
            set({ isLoadingOfferTypes: true, errorOfferTypes: null });
            try {
                const res = await getOfferTypesApi();
                if (!res.success) {
                    throw new Error('Failed to fetch Offer Types');
                }
                set({
                    offerTypes: res.payload.map((item) => ({
                        ...item,
                        id: item.name,
                        title: item.name,
                    })),
                    isLoadingOfferTypes: false
                })

            } catch (error) {
                set({ errorOfferTypes: error.message, isLoadingOfferTypes: false });
            }
        },
        fetchSubCatData: async () => {
            try {
                const res = await getProductCategoriesApi();
                if (!res.success) {
                    throw new Error('Failed to fetch product categories');
                }
                set({ subCatData: res.payload, isLoadingSubCat: false });
            } catch (error) {
                set({ errorSubCat: error.message, isLoadingSubCat: false });
            }
        }
    }), {
        name: 'data-storage',
        getStorage: () => localStorage,
    })
)